import {
  createRouter as createClientRouter,
  createWebHistory,
  createMemoryHistory,
  setupDataFetchingGuard,
} from 'vue-router/auto'
import { useAuth } from './stores/auth'
import { useUserSession } from './stores/userSession'
import * as Sentry from '@sentry/vue'
/*
 * By default, this plugins checks the folder at src/pages for any .vue files
 * and generates the corresponding routing structure basing itself in the file name.
 * This way, you no longer need to maintain a routes array when adding routes to your application,
 * instead just add the new .vue component to the routes folder and let this plugin do the rest!
 *
 * Let's take a look at a simple example:
 *
 * src/pages/
 * ├── index.vue
 * ├── about.vue
 * └── users/
 *     ├── index.vue
 *     └── [id].vue
 * This will generate the following routes:
 *
 * /: -> renders the index.vue component
 * /about: -> renders the about.vue component
 * /users: -> renders the users/index.vue component
 * /users/:id: -> renders the users/[id].vue component. id becomes a route param.
 *
 * View more examples:
 * @see https://github.com/posva/unplugin-vue-router#routes-folder-structure
 */
export function createRouter() {
  const router = createClientRouter({
    /**
     * If you need to serve vuero under a subdirectory,
     * you have to set the name of the directory in createWebHistory here
     * and update "base" config in vite.config.ts
     */
    // history: createWebHistory('my-subdirectory'),
    history: import.meta.env.SSR ? createMemoryHistory() : createWebHistory(),

    // extendRoutes: (routes) => {
    //   // Auth-callback route is now optional, can be removed or kept as fallback
    //   routes.push({
    //     path: '/auth/callback',
    //     name: 'auth-callback',
    //     component: () => import('./pages/auth/callback.vue'),
    //     meta: {
    //       requiresAuth: false,
    //     },
    //   })
    //   return routes
    // },

    scrollBehavior: (to, from, savedPosition) => {
      if (to.hash) {
        if (to.hash === '#') {
          return {
            top: 0,
            behavior: 'smooth',
          }
        }

        const el = document.querySelector(to.hash)
        if (el) {
          const top = parseFloat(getComputedStyle(el).scrollMarginTop)
          if (el instanceof HTMLElement) {
            el.focus()
          }
          return {
            el: to.hash,
            behavior: 'smooth',
            top,
          }
        }
        return {
          el: to.hash,
          behavior: 'smooth',
        }
      }

      if (savedPosition) {
        return savedPosition
      }
      else if (to.path !== from.path) {
        return { top: 0 }
      }
    },
  })

  setupDataFetchingGuard(router)

  router.beforeEach(async (to, from, next) => {
    // console.log('\n=== ROUTER GUARD START ===')
    // console.log('From:', from.path)
    // console.log('To:', to.path)
    // console.log('Query params:', to.query)

    const session = useUserSession()
    const auth = useAuth()
    const logged = !!session.isLoggedIn

    const path = to.path.toLowerCase()
    const isReport = path.startsWith('/report')
    const isAuth = path.startsWith('/auth')
    const isCallback = path === '/auth/callback'
    const isRoot = path === '/'

    // Handle token parameter in URL for auto-login
    const tokenParam = to.query.token
    if (tokenParam && !logged) {
      // console.log('\n=== TOKEN PARAMETER AUTO-LOGIN ===')
      // console.log('Token parameter detected, performing auto-login')
      
      try {
        // Set the token from URL parameter
        session.setToken(tokenParam as string)

        // Fetch user profile using the token
        await auth.fetchMe()

        // Set Sentry user
        if (session.user?.email) {
          Sentry.setUser({
            email: session.user.email,
          })
        }

        // console.log('Token auto-login successful')

        // Redirect to same page, removing token parameter
        return next({
          path: to.path,
          query: { ...to.query, token: undefined },
          replace: true
        })
      } catch (err) {
        console.error('\n=== ERROR IN TOKEN AUTO-LOGIN ===')
        console.error('Error:', err)

        // If token login fails, continue with normal flow
        session.logoutUser()
      }
    }

    // Handle bypass parameter for auto-login
    const bypassParam = to.query.bypass
    if (bypassParam === 'true' && !logged) {
      // console.log('\n=== BYPASS AUTO-LOGIN ===')
      // console.log('Bypass parameter detected, performing auto-login')

      try {
        // Set the specific JWT token for bypass mode
        const bypassToken = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6IjQ1ZGEwM2RiLTY5NDAtNDhmYS05Mzk0LWRmMmE1ZjYyMGM4YSIsImVtYWlsIjoidGF6dWthc2VucGFpQGdtYWlsLmNvbSJ9.E97Ybx_bVuGCIxSV2u72ebxxKuu0qdgfYnjCEkUPCJY'

        // Set the bypass token
        session.setToken(bypassToken)

        // Fetch user profile using the token
        await auth.fetchMe()

        // Set Sentry user for bypass mode
        if (session.user?.email) {
          Sentry.setUser({
            email: session.user.email,
          })
        }

        // console.log('Bypass auto-login successful')

        // Redirect to report page, removing bypass parameter
        const targetPath = isRoot ? '/report' : to.path
        return next({
          path: targetPath,
          query: { ...to.query, bypass: undefined },
          replace: true
        })
      } catch (err) {
        console.error('\n=== ERROR IN BYPASS AUTO-LOGIN ===')
        console.error('Error:', err)

        // If bypass fails, continue with normal flow
        session.logoutUser()
      }
    }

    // console.log('Route Analysis:')
    // console.log('- Path:', path)
    // console.log('- Route name:', to.name)
    // console.log('- Logged in:', logged)
    // console.log('- Is Root:', isRoot)
    // console.log('- Has access_token:', !!to.query.access_token)

    // CRITICAL: Check for SSO tokens (especially for root path)
    if (to.query.access_token && (isRoot || !isCallback)) {
      // console.log('\n=== PROCESSING SSO TOKENS ===')
      // console.log('Processing at route:', path)

      try {
        // Save tokens to user session
        // console.log('Saving access token...')
        session.setToken(to.query.access_token as string)

        // Save refresh token if provided
        if (to.query.refresh_token) {
          // console.log('Saving refresh token...')
          session.setRefreshToken(to.query.refresh_token as string)
        }

        // Fetch user profile using the token
        // console.log('Fetching user profile...')
        await auth.fetchMe()
        // console.log('User profile fetched:', session.user)

        // Set Sentry user immediately after successful auth
        if (session.user?.email) {
          Sentry.setUser({
            email: session.user.email,
          })
          // console.log('Sentry user set')
        }

        // Determine redirect path
        let redirectPath = '/report'

        // If we're at root, always go to report
        if (isRoot) {
          // console.log('Root path detected, redirecting to /report')
          redirectPath = '/report'
        } else if (to.query.redirect) {
          // console.log('Redirect query param found:', to.query.redirect)
          redirectPath = to.query.redirect as string
        } else if (!isAuth && !isCallback) {
          // Keep the current path if it's not auth-related
          redirectPath = to.fullPath.split('?')[0]
          // console.log('Keeping current path (cleaned):', redirectPath)
        }

        // console.log('Final redirect path:', redirectPath)

        // Redirect without query parameters
        // console.log('Redirecting with next()...')
        return next({
          path: redirectPath,
          replace: true // This will replace current history entry
        })
      } catch (err) {
        console.error('\n=== ERROR PROCESSING SSO TOKENS ===')
        console.error('Error:', err)

        // Clear any tokens that might have been set
        session.logoutUser()

        // Redirect to SSO login again
        const ssoLoginUrl = import.meta.env.VITE_BASE_SSO
        const returnUrl = `${window.location.origin}/report`
        // console.log('Redirecting back to SSO:', `${ssoLoginUrl}?redirect_uri=${encodeURIComponent(returnUrl)}`)

        window.location.href = `${ssoLoginUrl}?redirect_uri=${encodeURIComponent(returnUrl)}`
        return false
      }
    }

    // If user is already logged in and accessing agent routes, redirect to report
    if (logged && to.name && String(to.name).includes('agent')) {
      // console.log('\n=== AGENT ROUTE REDIRECT ===')
      // console.log('Caught dynamic [agent] route, redirecting to report')
      return next({ path: '/report' })
    }

    // If not logged in and trying to access protected route, redirect to SSO login
    if (!logged && !isCallback && !isAuth) {
      // console.log('\n=== SSO LOGIN REDIRECT ===')
      // console.log('User not logged in, redirecting to SSO')

      // Special handling for root path - always redirect to report after login
      const targetPath = isRoot ? '/report' : to.fullPath

      // Redirect directly to SSO login
      const ssoLoginUrl = import.meta.env.VITE_BASE_SSO
      const returnUrl = `${window.location.origin}${targetPath}`

      // console.log('SSO URL:', `${ssoLoginUrl}?redirect_uri=${encodeURIComponent(returnUrl)}`)

      // Redirect to SSO login
      window.location.href = `${ssoLoginUrl}?redirect_uri=${encodeURIComponent(returnUrl)}`
      return false // Prevent further navigation
    }

    // If logged in and accessing root or routes other than /report/* or /auth/*,
    // redirect to the report page
    if (logged && (isRoot || (!isReport && !isAuth && !isCallback))) {
      // console.log('\n=== STANDARD REPORT REDIRECT ===')
      // console.log('Redirecting to report page')
      return next({ path: '/report' })
    }

    // Set Sentry user if logged in
    if (logged && session.user?.email) {
      Sentry.setUser({
        email: session.user.email,
      })
    }

    // console.log('\n=== NORMAL NAVIGATION ===')
    // console.log('Proceeding with normal navigation\n')

    // Continue with normal navigation
    return next()
  })

  return router
}
